<script setup lang="ts">
import ClubAccessSettings from '../../../js/dto/club/ClubAccessSettings.js';
import ErrorModel from '../../../js/model/Error.js';
import KgrCheckbox from '../../common/form/checkbox/Checkbox.vue';
import KgrFormRow from '../../common/form/FormRow.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const accessSettings = defineModel('accessSettings', {
    type: ClubAccessSettings,
    required: true,
});

defineProps({
    error: {
        type: ErrorModel,
        required: true,
    },
});
</script>

<template>
    <div>
        <div class="access-settings-columns">
            <div class="access-settings-column">
                <fieldset>
                    <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                        {{ $t('Automatische Abmeldung') }}
                    </legend>
                    <kgr-form-row
                        :label="$t('Minuten bis zum automatischen Logout des Benutzers')"
                        :tooltip="$t('Hier kann definiert werden nach wie viel Minuten Inaktivität ein Benutzer automatisch ausgeloggt werden soll. Der Wert kann zwischen 1 Minute und 240 Minuten liegen.')"
                        :error="error"
                        field-name="autoLogoutTime"
                    >
                        <input
                            v-model.number="accessSettings.autoLogoutTime"
                            type="number"
                            class="kgr-input"
                            min="0"
                            max="240"
                        />
                    </kgr-form-row>
                </fieldset>

                <fieldset v-if="accessSettings.hasPhysicalAccessModule">
                    <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                        {{ $t('Zutrittskontrolle') }}
                    </legend>
                    <kgr-form-row
                        :label="$t('Stunde zu der alle noch anwesenden Mitglieder als abwesend markiert werden')"
                        :error="error"
                        field-name="physicalAccessStateResetHour"
                    >
                        <input
                            v-model.number="accessSettings.physicalAccessStateResetHour"
                            type="number"
                            class="kgr-input"
                            min="0"
                            max="23"
                        />
                    </kgr-form-row>
                </fieldset>

                <fieldset>
                    <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                        {{ $t('Mitgliedsausweis in der App') }}
                    </legend>
                    <kgr-checkbox
                        v-model="accessSettings.onlineMembershipTerminationEnabled"
                        :label="$t('club.accessSettings.onlineMembershipTerminationEnabled')"
                        class="kgr-row"
                    />
                    <kgr-form-row
                        :error="error"
                        field-name="memberSinceDateStrategy"
                    >
                        <label class="kgr-radio">
                            <input
                                v-model="accessSettings.memberSinceDateStrategy"
                                type="radio"
                                value="current_contract"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Startdatum des aktuellen Vertrags anzeigen') }}</span>
                        </label>
                        <label class="kgr-radio mt1">
                            <input
                                v-model="accessSettings.memberSinceDateStrategy"
                                type="radio"
                                value="oldest_gapless_contract"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Startdatum des ältesten Vertrags ohne Unterbrechung anzeigen') }}</span>
                        </label>
                    </kgr-form-row>
                </fieldset>
            </div>

            <div class="access-settings-column">
                <fieldset>
                    <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                        {{ $t('Zugriffsrechte für App-User') }}
                    </legend>
                    <kgr-checkbox
                        v-model="accessSettings.membersEditOwnData"
                        :label="$t('App-User dürfen ihre Kontaktdaten selbst bearbeiten')"
                        class="kgr-row"
                    />
                    <kgr-checkbox
                        v-model="accessSettings.membersCanViewInvoices"
                        :label="$t('Ordner Beitragsrechnungen in der Vereins-App anzeigen')"
                        class="kgr-row"
                    />
                    <div v-if="accessSettings.membersCanViewInvoices">
                        <p class="kgr-text kgr-text--small">{{ $t('club.accessSettings.invoiceNotificationTiming.description') }}</p>
                        <label class="kgr-radio">
                            <input
                                v-model="accessSettings.invoiceNotificationTiming"
                                type="radio"
                                value="immediately"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Direkt nach Erstellung') }}</span>
                        </label>
                        <label class="kgr-radio mt1">
                            <input
                                v-model="accessSettings.invoiceNotificationTiming"
                                type="radio"
                                value="after_days"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Nach X Tagen') }}</span>
                        </label>
                        <kgr-form-row
                            v-if="accessSettings.invoiceNotificationTiming === 'after_days'"
                            :error="error"
                            field-name="invoiceNotificationDays"
                            inline
                        >
                            <input
                                v-model.number="accessSettings.invoiceNotificationDays"
                                class="kgr-input mt1"
                                type="number"
                                min="1"
                            />
                            <p class="kgr-text kgr-text--small">{{ $t('Wird die Einstellung direkt nach einem Rechnungslauf verändert, ist zu beachten, dass Mitglieder ggf. über neue Rechnungen benachrichtigt wurden, die dann nicht mehr sichtbar sind.') }}</p>
                        </kgr-form-row>
                    </div>
                </fieldset>

                <fieldset>
                    <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                        {{ $t('Sonstiges') }}
                    </legend>
                    <kgr-checkbox
                        v-model="accessSettings.sendBirthdayGreetings"
                        :label="$t('Geburtstagsgrüße automatisch versenden')"
                        class="kgr-row"
                    />
                    <span class="kgr-text kgr-text--small">{{ $t('club.accessSettings.sendBirthdayGreetings.tooltip') }}</span>
                    <kgr-form-row
                        :label="$t('Zahlungsintervall für neue Mitglieder')"
                        :error="error"
                        field-name="defaultPaymentInterval"
                        class="mt2"
                    >
                        <select
                            v-model="accessSettings.defaultPaymentInterval"
                            class="kgr-select"
                        >
                            <option value="monthly">{{ $t('monatlich') }}</option>
                            <option value="quarterly">{{ $t('quartalsweise') }}</option>
                            <option value="half-yearly">{{ $t('halbjährlich') }}</option>
                            <option value="yearly">{{ $t('jährlich') }}</option>
                        </select>
                    </kgr-form-row>
                </fieldset>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss" rel="stylesheet/scss">
.access-settings-columns {
    display: flex;
    gap: 3rem;
    align-items: flex-start;
}

.access-settings-column {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2rem;
}
</style>
